import React, { useState, useEffect } from "react";
import {
  Table,
  TableRow,
  TableBody,
  useTheme,
  Box,
  Typography,
  TextField,
  TableContainer,
  CircularProgress,
} from "@mui/material";
import ErrorIcon from "@mui/icons-material/Error";
import VantageListHead from "../../../component/ListHead/SignalList";
import {
  Tablecell,
  applySortFilter,
  getComparator,
  hashInvestment,
  hashInvestmentReverse,
} from "../../../component/Methods";
import MarketDataUnavailable from "../Popup/MarketDataUnavailable";
import {
  formatTimeAgo,
  formatTimeAgo5minutes,
  getStrategiesContainingTicker,
} from "../../../utils/helpers";
import { findLivePriceForTicker, formatTickerDisplay } from "../../../utils/livePriceTickerMatch";

import { useDispatch } from "react-redux";
import { sendWebSocketMessage } from "../../../hooks/useWebSocket";

const SignalsLimitedByLocates = ({
  dummy,
  tableHeader,
  showPopup,
  marketData,
  strategyListToggle,
  markets,
  isStrategist,
  isInvestor,
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [openPopup, setOpenPopup] = useState(false);
  const [data, setData] = useState([]);
  const [updatedData, setUpdatedData] = useState([]);
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("expiration");
  const [selectedRow, setSelectedRow] = useState(null);
  const [unvailableStratgies, setUnvailableStratgies] = useState([]);
  const [editingPrice, setEditingPrice] = useState(null);

  const handleRowClick = (row) => {
    if (showPopup && !row.marketData) {
      setSelectedRow(row);
      setOpenPopup(true);
      const unvailableStratgies = getStrategiesContainingTicker(
        strategyListToggle,
        row?.ticker
      );
      setUnvailableStratgies(unvailableStratgies);
    }
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };
  useEffect(() => {
    const updatedData = dummy?.map(({ ticker, ...rest }) => {
      const matchingMarketData = findLivePriceForTicker(marketData, ticker);

      const marketDataAvailable =
        matchingMarketData &&
        formatTimeAgo5minutes(matchingMarketData?.expiration)?.msg === "true";

      let marketName = "";

      if (markets) {
        const matchedMarket = markets.find(
          (market) => market?.marketId == matchingMarketData?.market
        );

        if (matchedMarket) {
          marketName = matchedMarket?.name;
          if (marketName == "FMP") {
            marketName = "Primary";
          }
        }
      }
      const expirationTime = new Date(matchingMarketData?.expiration * 1000);
      const currentTime = new Date();
      const timeDifferenceInMinutes = Math.floor(
        (currentTime - expirationTime) / 1000 / 60
      );
      const expired = timeDifferenceInMinutes > 60;

      return {
        ticker: formatTickerDisplay(ticker),
        investment: hashInvestment(ticker.investment),
        marketData: Boolean(marketDataAvailable),
        price: matchingMarketData?.price?.toFixed(2),
        marketDataDetails: matchingMarketData,
        bloomberg: marketName,
        expiration: matchingMarketData?.expiration,
        expired,
        date: matchingMarketData?.date || null,
        ...rest,
      };
    });

    const filteredUsers = applySortFilter(
      updatedData,
      getComparator(order, orderBy)
    );

    setData(filteredUsers);
  }, [order, orderBy, dummy, marketData, markets]);

  const handleEditRow = (index, field, value) => {
    setData((prevData) => {
      const updatedData = [...prevData];
      updatedData[index] = { ...updatedData[index], [field]: value };
      setUpdatedData((prevUpdatedData) => {
        const updatedRow = updatedData[index];
        const isAlreadyUpdated = prevUpdatedData.some(
          (item) => item.ticker === updatedRow.ticker
        );

        if (isAlreadyUpdated) {
          return prevUpdatedData.map((item) =>
            item.ticker === updatedRow.ticker ? updatedRow : item
          );
        } else {
          return [...prevUpdatedData, updatedRow];
        }
      });

      return updatedData;
    });
  };

  const handlePriceClick = (index) => {
    setEditingPrice(index);
  };

  const handlePriceBlur = () => {
    setEditingPrice(null);
  };
  const handleMarketPrice = () => {
    if (markets && markets.length > 0) {
      const market = markets?.find((i) => i.type == 1);

      if (!market) {
        console.error("Market with type 1 not found");
        return;
      }

      const marketId = market.marketId;
      let keys = {};
      let values = {};

      updatedData?.forEach((item) => {
        const { ticker, price, investment } = item;

        keys[ticker] = {
          symbol: ticker,
          currency: 7,
          exchange: 4,
          investment: hashInvestmentReverse(investment),
        };

        const currentDate = Math.floor(Date.now() / 1000);

        values[ticker] = {
          [9]: {
            [marketId]: {
              date: currentDate,
              value: parseFloat(price),
            },
          },
          [10]: {
            [marketId]: {
              date: currentDate,
              value: parseFloat(price),
            },
          },
        };
      });

      const socketRequest = {
        action: "add_market_data",
        data: {
          keys,
          values,
        },
      };
      // dispatch(addActiveRequests("add_market_data"));
      // dispatch(updateLoading(true));
      // dispatch(updateLoadingMsg("Updating Market Price"));
      sendWebSocketMessage(JSON.stringify(socketRequest));
    }
  };
  return (
    <>
      {data && data?.length > 0 ? (
        <Box
          sx={{
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: "4px",
            width: "100%",
            display: "flex",
            flexDirection: "column",
            backgroundColor: theme.palette.background.paper,
          }}
        >
          <Typography
            color="primary"
            sx={{
              fontSize: "15px",
              textAlign: "right",
              marginTop: "20px",
              textDecoration: "none",
              color: theme.palette.primary.main,
              flexShrink: 0,
              "&:hover": {
                cursor: "pointer",
                textDecoration: "underline",
                textUnderlineOffset: "2px",
              },
            }}
            onClick={handleMarketPrice}
          >
            Click here to save manually entered prices
          </Typography>

          <TableContainer sx={{ width: "100%", overflowX: "hidden" }}>
            <Table
              size="small"
              stickyHeader
              aria-label="a dense table"
              sx={{ width: "100%", tableLayout: "fixed" }}
            >
              <VantageListHead
                order={order}
                orderBy={orderBy}
                headLabel={tableHeader}
                noStatus={true}
                customizeFont={12}
                rowCount={data.length}
                onRequestSort={handleRequestSort}
              />

              <TableBody>
                {data?.map((row, index) => (
                  <TableRow
                    key={index}
                    sx={{
                      backgroundColor: theme.palette.background.paper,
                      "&:hover": {
                        background: theme.palette.action.hover,
                        cursor: "pointer",
                      },
                    }}
                  >
                    <Tablecell
                      align="left"
                      sx={{
                        fontSize: "12px",
                        paddingLeft: "5px",
                        borderLeft: "none",
                        borderRight: "none",
                        color: theme.palette.text.primary,
                        backgroundColor: theme.palette.background.paper,
                        borderColor: theme.palette.divider,
                        width: "18%",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {row.ticker?.toUpperCase() || ""}
                    </Tablecell>
                    <Tablecell
                      align="left"
                      sx={{
                        fontSize: "12px",
                        paddingLeft: "5px",
                        borderLeft: "none",
                        borderRight: "none",
                        color: theme.palette.text.primary,
                        backgroundColor: theme.palette.background.paper,
                        borderColor: theme.palette.divider,
                        width: "18%",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {row.investment}
                    </Tablecell>
                    <Tablecell
                      align="right"
                      sx={{
                        fontSize: "12px",
                        paddingRight: "5px",
                        borderLeft: "none",
                        borderRight: "none",
                        cursor: "pointer",
                        color: theme.palette.text.primary,
                        backgroundColor: theme.palette.background.paper,
                        borderColor: theme.palette.divider,
                        width: "16%",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                      onClick={() => handlePriceClick(index)}
                    >
                      {editingPrice === index ? (
                        <TextField
                          value={row?.price}
                          onChange={(e) =>
                            handleEditRow(index, "price", e.target.value)
                          }
                          onBlur={handlePriceBlur}
                          size="small"
                          variant="standard"
                          autoFocus
                          InputProps={{
                            disableUnderline: true,
                            style: {
                              fontSize: "12px",
                              width: "10ch",
                              marginRight: "0.5ch",
                              paddingTop: "0px",
                              paddingBottom: "0px",
                              color: theme.palette.text.primary,
                            },
                          }}
                          inputProps={{
                            type: "number",
                            step: "any",
                            style: {
                              textAlign: "right",
                              paddingTop: "0px",
                              paddingBottom: "0px",
                              color: theme.palette.text.primary,
                              MozAppearance: "textfield", // For Firefox
                              WebkitAppearance: "none", // For most other browsers
                              // For hiding the spin buttons specifically in WebKit browsers
                              "&::WebkitOuterSpinButton": {
                                WebkitAppearance: "none",
                                margin: 0,
                              },
                              "&::WebkitInnerSpinButton": {
                                WebkitAppearance: "none",
                                margin: 0,
                              },
                            },
                          }}
                        />
                      ) : (
                        <Typography
                          sx={{
                            color: theme.palette.text.primary,
                          }}
                        >
                          {row?.price}
                        </Typography>
                      )}
                    </Tablecell>

                    <Tablecell
                      align="right"
                      sx={{
                        fontSize: "12px",
                        paddingLeft: "5px",
                        borderLeft: "none",
                        borderBottom: "none",
                        borderRight: "none",
                        color: row.expired ? theme.palette.error.main : theme.palette.success.main,
                        paddingRight: "10px",
                        backgroundColor: theme.palette.background.paper,
                        borderColor: theme.palette.divider,
                        width: "48%",
                        overflow: "hidden",
                      }}
                      onClick={() => handleRowClick(row)}
                    >
                      {row.marketData && row?.price != 0 ? (
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            width: "100%",
                            gap: 1,
                            minWidth: 0,
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: "12px",
                              color: row.expired ? theme.palette.error.main : theme.palette.success.main,
                              textAlign: "left",
                              minWidth: 0,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              flex: 1,
                            }}
                            variant="body2"
                          >
                            {
                              formatTimeAgo(row.marketDataDetails?.expiration)
                                ?.msg
                            }
                          </Typography>

                          {!(isStrategist || isInvestor) && (
                            <Typography
                              sx={{
                                fontSize: "12px",
                                color: row.expired ? theme.palette.error.main : theme.palette.success.main,
                                textAlign: "right",
                                minWidth: 0,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                flexShrink: 0,
                              }}
                              variant="body2"
                            >
                              {row?.bloomberg}
                            </Typography>
                          )}
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            minWidth: 0,
                          }}
                        >
                          <CircularProgress size={15} color="error" />
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: "12px",
                              color: theme.palette.error.main,
                            }}
                          >
                            Loading...
                          </Typography>
                        </Box>
                      )}
                    </Tablecell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      ) : null}

      {selectedRow && (
        <MarketDataUnavailable
          open={openPopup}
          onClose={() => setOpenPopup(false)}
          row={selectedRow}
          unvailableStratgies={unvailableStratgies}
        />
      )}
    </>
  );
};

export default SignalsLimitedByLocates;
